import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'

interface Props {
  children: React.ReactNode
}

/**
 * Ensures that the user is authenticated before rendering the contents.
 *
 * If the user is not authenticated, the user is redirected to the sign-in page.
 */
export const EnsureAuth: React.FC<Props> = ({ children }) => {
  const { data, isPending } = useQuery(getUserQuery)
  const {
    state: {
      location: { href },
    },
    navigate,
  } = useRouter()
  /**
   * Is the authentication status determined?
   */
  const isDetermined = !isPending

  const isAuthenticated = isDetermined && !!data

  useEffect(() => {
    // Wait while pending. Authentication status is not determined yet.
    if (!isDetermined) {
      return
    }

    // Redirect to sign-in if no user data is available.
    if (!isAuthenticated) {
      localStorage.removeItem(LOCAL_STORAGE_KEY_MAP.token)

      void navigate({
        to: '/sign-in',
        search: {
          redirect: href,
        },
      })
    }
  }, [isDetermined, isAuthenticated, href, navigate])

  if (!isAuthenticated) {
    return null
  }

  return children
}
